/**
 * DeviceCatalogManager — Catálogo de dispositivos (marcas y modelos).
 *
 * POR QUÉ EXISTE
 * ---------------------------------------------------------------------------
 * Las fichas (servidores, switches, firewalls, estaciones…) ya pedían marca y
 * modelo, pero esos valores solo se podían "mantener" desde el desplegable de
 * cada ficha: no había pantalla para darlos de alta, corregirlos, ver cuántos
 * equipos usan cada modelo ni definir su plantilla (puertos, PoE, PSU…). El
 * resultado era un catálogo que crecía solo con el inventario y nadie podía
 * curar.
 *
 * QUÉ HACE
 * ---------------------------------------------------------------------------
 * Dos vistas en el mismo listado estándar:
 *   · **Modelos** (por defecto): marca, tipo, ámbito, equipos que lo usan,
 *     resumen de la plantilla y documentación adjunta.
 *   · **Marcas**: nombre, tipo, ámbito, equipos y alias de inventario.
 *
 * Cada entrada tiene un ÁMBITO: global (empresa madre), de la empresa o
 * privado del usuario. Un `editor` crea en privado y no ensucia el catálogo
 * global; un administrador puede compartir con su empresa o publicar global.
 *
 * La plantilla del modelo es lo que usan las fichas y el inventario automático:
 * número y tipo de puertos, PoE, PSU, tamaño en U y consumo.
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  CpuChipIcon,
  TagIcon,
  ArrowPathIcon,
  ChartBarIcon,
  DocumentTextIcon,
  PlusIcon,
  Squares2X2Icon,
  ShareIcon,
} from '@heroicons/react/24/outline';
import ListPage from './common/ListPage';
import Dialog from './common/Dialog';
import api from '../services/api';

const SCOPE_OPTIONS = [
  { value: 'private', label: 'Privado (solo yo)' },
  { value: 'company', label: 'De la empresa' },
  { value: 'global', label: 'Global (empresa madre)' },
];

const SCOPE_LABEL = {
  private: 'Privado',
  company: 'De la empresa',
  global: 'Global',
};

const PORT_TYPE_OPTIONS = [
  { value: 'rj45', label: 'RJ45 (cobre)' },
  { value: 'sfp', label: 'SFP' },
  { value: 'sfp+', label: 'SFP+' },
  { value: 'sfp28', label: 'SFP28' },
  { value: 'qsfp', label: 'QSFP' },
  { value: 'qsfp28', label: 'QSFP28' },
  { value: 'fibra', label: 'Fibra' },
  { value: 'consola', label: 'Consola' },
];

const SPEED_OPTIONS = [
  { value: '', label: 'Sin especificar' },
  { value: '100M', label: '100 Mbps' },
  { value: '1G', label: '1 Gbps' },
  { value: '2.5G', label: '2,5 Gbps' },
  { value: '10G', label: '10 Gbps' },
  { value: '25G', label: '25 Gbps' },
  { value: '40G', label: '40 Gbps' },
  { value: '100G', label: '100 Gbps' },
];

const FORM_VACIO = {
  name: '',
  device_type: 'switch',
  manufacturer_id: '',
  manufacturer_name: '',
  scope: 'private',
  sku: '',
  aliases: '',
  size_u: '',
  power_watts: '',
  datasheet_url: '',
  image_url: '',
  notes: '',
  port_count: '',
  port_type: 'rj45',
  port_speed: '1G',
  port_poe: false,
  uplink_count: '',
  uplink_type: 'sfp+',
  uplink_speed: '10G',
  poe_capable: false,
  poe_budget_w: '',
  psu_count: '',
  psu_watts: '',
  psu_connector: '',
  psu_efficiency: '',
  psu_redundant: false,
};

const TIPOS_CON_PUERTOS = new Set(['switch', 'panel', 'pdu', 'router']);
const TIPOS_CON_PSU = new Set(['server', 'storage', 'chassis']);

function currentUser() {
  try {
    return JSON.parse(localStorage.getItem('user') || '{}');
  } catch {
    return {};
  }
}

function isAdminUser(user) {
  if (user?.is_superadmin) return true;
  const role = typeof user?.app_role === 'string'
    ? user.app_role
    : (typeof user?.role === 'string' ? user.role : '');
  return ['owner', 'admin'].includes(role.toLowerCase());
}

function numberOrNull(value) {
  const parsed = Number(value);
  return Number.isFinite(parsed) && value !== '' ? parsed : null;
}

/** Formulario → plantilla (`spec`) que entienden las fichas y el agente. */
function buildSpec(form) {
  const spec = {};
  const access = numberOrNull(form.port_count) || 0;
  const uplinks = numberOrNull(form.uplink_count) || 0;
  const total = access + uplinks;
  const conPuertosFisicos = ['switch', 'panel', 'router'].includes(form.device_type);

  if (total > 0) spec.port_count = total;
  if (access > 0 && conPuertosFisicos) {
    spec.ports = [
      {
        from: 1,
        to: access,
        type: form.port_type || 'rj45',
        ...(form.port_speed ? { speed: form.port_speed } : {}),
        ...(form.port_poe ? { poe: true } : {}),
      },
    ];
    if (uplinks > 0) {
      spec.ports.push({
        from: access + 1,
        to: total,
        type: form.uplink_type || 'sfp+',
        ...(form.uplink_speed ? { speed: form.uplink_speed } : {}),
      });
    }
  }

  if (form.poe_capable || form.port_poe) spec.poe_capable = true;
  if (numberOrNull(form.poe_budget_w)) spec.poe_budget_w = Number(form.poe_budget_w);
  if (numberOrNull(form.size_u)) spec.size_u = Number(form.size_u);
  if (numberOrNull(form.power_watts)) spec.power_watts = Number(form.power_watts);

  const psuCount = numberOrNull(form.psu_count);
  if (psuCount) {
    spec.psu = { count: psuCount };
    if (numberOrNull(form.psu_watts)) spec.psu.watts = Number(form.psu_watts);
    if (form.psu_connector) spec.psu.connector = form.psu_connector;
    if (form.psu_efficiency) spec.psu.efficiency = form.psu_efficiency;
    spec.psu.redundant = Boolean(form.psu_redundant);
  }
  return spec;
}

/** Modelo de la API → formulario (para editar sin perder la plantilla). */
function formFromModel(row) {
  const spec = row.spec || {};
  const blocks = Array.isArray(spec.ports) ? spec.ports : [];
  const access = blocks[0] || {};
  const uplink = blocks.find((block) => Number(block.from) > Number(access.to || 0));
  const accessCount = access.to ? Number(access.to) : (spec.port_count || '');
  const uplinkCount = uplink ? Number(uplink.to) - Number(uplink.from) + 1 : '';
  const psu = spec.psu || {};

  return {
    ...FORM_VACIO,
    name: row.name || '',
    device_type: row.device_type || 'switch',
    manufacturer_id: row.manufacturer_id ? String(row.manufacturer_id) : '',
    manufacturer_name: '',
    scope: row.scope || 'private',
    sku: row.sku || '',
    aliases: (row.aliases || []).join(', '),
    size_u: spec.size_u ?? row.size_u ?? '',
    power_watts: spec.power_watts ?? row.power_watts ?? '',
    datasheet_url: row.datasheet_url || '',
    image_url: row.image_url || '',
    notes: row.notes || '',
    port_count: accessCount,
    port_type: access.type || 'rj45',
    port_speed: access.speed || '',
    port_poe: Boolean(access.poe),
    uplink_count: uplinkCount,
    uplink_type: uplink?.type || 'sfp+',
    uplink_speed: uplink?.speed || '',
    poe_capable: Boolean(spec.poe_capable),
    poe_budget_w: spec.poe_budget_w ?? '',
    psu_count: psu.count ?? '',
    psu_watts: psu.watts ?? '',
    psu_connector: psu.connector || '',
    psu_efficiency: psu.efficiency || '',
    psu_redundant: Boolean(psu.redundant),
  };
}

function formFromManufacturer(row) {
  return {
    ...FORM_VACIO,
    name: row.name || '',
    device_type: row.device_type || '',
    scope: row.scope || 'private',
    aliases: (row.aliases || []).join(', '),
    notes: row.notes || '',
  };
}

function specResumen(row) {
  const spec = row.spec || {};
  const parts = [];
  if (spec.port_count) parts.push(`${spec.port_count} puertos`);
  if (spec.poe_capable) {
    parts.push(spec.poe_budget_w ? `PoE ${spec.poe_budget_w} W` : 'PoE');
  }
  if (spec.psu?.count) parts.push(`${spec.psu.count}× PSU`);
  return parts.length ? parts.join(' · ') : '—';
}

function scopeBadge(scope) {
  if (scope === 'global') {
    return <span className="list-badge list-badge--muted" title="La comparten todas las empresas">Global</span>;
  }
  if (scope === 'company') {
    return <span className="list-badge list-badge--accent">De la empresa</span>;
  }
  return <span className="list-badge" title="Solo lo ve su creador">Privado</span>;
}

export default function DeviceCatalogManager({ companyId }) {
  const navigate = useNavigate();
  const user = useMemo(currentUser, []);
  const admin = useMemo(() => isAdminUser(user), [user]);

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [aviso, setAviso] = useState('');

  const [models, setModels] = useState([]);
  const [manufacturers, setManufacturers] = useState([]);
  const [deviceTypes, setDeviceTypes] = useState([]);
  const [vista, setVista] = useState('models');

  const [search, setSearch] = useState('');
  const [filterType, setFilterType] = useState('');
  const [filterScope, setFilterScope] = useState('');
  const [filterManufacturer, setFilterManufacturer] = useState('');
  const [filterEnabled, setFilterEnabled] = useState('');

  const [formOpen, setFormOpen] = useState(false);
  const [editando, setEditando] = useState(null);
  const [form, setForm] = useState(FORM_VACIO);
  const [guardando, setGuardando] = useState(false);

  const [uso, setUso] = useState(null);
  const [copiando, setCopiando] = useState(null);
  const [nombreCopia, setNombreCopia] = useState('');

  const cargar = useCallback(async () => {
    if (!companyId) return;
    setLoading(true);
    setError('');
    try {
      const [modelsResp, mfrResp, typesResp] = await Promise.all([
        api.getDeviceModels(companyId, { includeInactive: true, withUsage: true }),
        api.getManufacturers(companyId, { includeInactive: true, withUsage: true }),
        api.getCatalogDeviceTypes(companyId).catch(() => ({ data: [] })),
      ]);
      setModels(modelsResp.data || []);
      setManufacturers(mfrResp.data || []);
      const tipos = typesResp.data || [];
      setDeviceTypes(tipos.length ? tipos : [
        { value: 'server', label: 'Servidor' },
        { value: 'switch', label: 'Switch' },
        { value: 'network', label: 'Red (genérico)' },
        { value: 'storage', label: 'Almacenamiento' },
        { value: 'workstation', label: 'Estación de trabajo' },
      ]);
    } catch (err) {
      setError(err.message || 'No se pudo cargar el catálogo');
    } finally {
      setLoading(false);
    }
  }, [companyId]);

  useEffect(() => {
    setForm(FORM_VACIO);
    setEditando(null);
    cargar();
  }, [cargar]);

  const mostrarAviso = (texto) => {
    setAviso(texto);
    setTimeout(() => setAviso(''), 2500);
  };

  const etiquetaTipo = useCallback(
    (value) => deviceTypes.find((tipo) => tipo.value === value)?.label || value || '—',
    [deviceTypes],
  );

  /* ── Filtrado ─────────────────────────────────────────────────────────── */
  const modelosFiltrados = useMemo(() => {
    const term = search.trim().toLowerCase();
    return models.filter((row) => {
      if (filterType && row.device_type !== filterType) return false;
      if (filterScope && row.scope !== filterScope) return false;
      if (filterManufacturer && String(row.manufacturer_id) !== filterManufacturer) return false;
      if (filterEnabled === 'on' && !row.is_active) return false;
      if (filterEnabled === 'off' && row.is_active) return false;
      if (!term) return true;
      return [
        row.name,
        row.manufacturer_name,
        row.sku,
        etiquetaTipo(row.device_type),
        (row.aliases || []).join(' '),
      ].filter(Boolean).join(' ').toLowerCase().includes(term);
    });
  }, [models, search, filterType, filterScope, filterManufacturer, filterEnabled, etiquetaTipo]);

  const marcasFiltradas = useMemo(() => {
    const term = search.trim().toLowerCase();
    return manufacturers.filter((row) => {
      if (filterType && row.device_type && row.device_type !== filterType) return false;
      if (filterScope && row.scope !== filterScope) return false;
      if (filterEnabled === 'on' && !row.is_active) return false;
      if (filterEnabled === 'off' && row.is_active) return false;
      if (!term) return true;
      return [row.name, etiquetaTipo(row.device_type), (row.aliases || []).join(' ')]
        .filter(Boolean).join(' ').toLowerCase().includes(term);
    });
  }, [manufacturers, search, filterType, filterScope, filterEnabled, etiquetaTipo]);

  const kpis = useMemo(() => ({
    modelos: models.length,
    sinPlantilla: models.filter((row) => !row.spec || !Object.keys(row.spec).length).length,
    sinUso: models.filter((row) => (row.usage?.total || 0) === 0).length,
    privados: models.filter((row) => row.scope === 'private').length,
    globales: models.filter((row) => row.scope === 'global').length,
  }), [models]);

  /* ── Alta / edición ───────────────────────────────────────────────────── */
  const abrirAlta = () => {
    setEditando(null);
    setForm({ ...FORM_VACIO, device_type: vista === 'models' ? 'switch' : '', scope: admin ? 'company' : 'private' });
    setFormOpen(true);
  };

  const abrirEdicion = (row) => {
    setEditando(row);
    setForm(vista === 'manufacturers' ? formFromManufacturer(row) : formFromModel(row));
    setFormOpen(true);
  };

  const guardar = async () => {
    setGuardando(true);
    setError('');
    try {
      const esMarca = vista === 'manufacturers';
      const payload = {
        name: form.name,
        device_type: form.device_type || null,
        scope: form.scope,
        aliases: form.aliases,
        notes: form.notes || null,
      };

      if (esMarca) {
        if (editando) {
          await api.updateManufacturer(editando.id, payload, companyId);
        } else {
          await api.createManufacturer(payload, companyId);
        }
      } else {
        const modelo = {
          ...payload,
          manufacturer_id: form.manufacturer_id || null,
          manufacturer_name: form.manufacturer_id ? null : form.manufacturer_name,
          sku: form.sku || null,
          spec: buildSpec(form),
          size_u: numberOrNull(form.size_u),
          power_watts: numberOrNull(form.power_watts),
          datasheet_url: form.datasheet_url || null,
          image_url: form.image_url || null,
        };
        if (editando) {
          await api.updateDeviceModel(editando.id, modelo, companyId);
        } else {
          await api.createDeviceModel(modelo, companyId);
        }
      }

      setFormOpen(false);
      setEditando(null);
      setForm(FORM_VACIO);
      mostrarAviso(editando ? 'Cambios guardados' : 'Entrada creada');
      await cargar();
    } catch (err) {
      setError(err.message || 'No se pudo guardar');
    } finally {
      setGuardando(false);
    }
  };

  const borrar = async (row) => {
    try {
      if (vista === 'manufacturers') {
        await api.deleteManufacturer(row.id, companyId);
      } else {
        await api.deleteDeviceModel(row.id, companyId);
      }
      mostrarAviso('Entrada desactivada');
      await cargar();
    } catch (err) {
      setError(err.message || 'No se pudo eliminar');
    }
  };

  const abrirCopia = (row) => {
    setCopiando(row);
    setNombreCopia(`${row.name} (copia)`);
  };

  const confirmarCopia = async () => {
    if (!copiando) return;
    setGuardando(true);
    try {
      await api.cloneDeviceModel(copiando.id, { name: nombreCopia }, companyId);
      setCopiando(null);
      mostrarAviso('Modelo copiado');
      await cargar();
    } catch (err) {
      setError(err.message || 'No se pudo copiar');
    } finally {
      setGuardando(false);
    }
  };

  const verUso = async (row) => {
    setUso({ row, loading: true, data: null });
    try {
      const resp = await api.getDeviceModelUsage(row.id, companyId);
      setUso({ row, loading: false, data: resp.data || {} });
    } catch (err) {
      setError(err.message || 'No se pudo cargar el uso');
      setUso({ row, loading: false, data: null });
    }
  };

  const cambiarAmbito = async (row, scope) => {
    try {
      await api.setCatalogScope(vista === 'manufacturers' ? 'manufacturers' : 'models', row.id, scope, companyId);
      mostrarAviso('Ámbito actualizado');
      await cargar();
    } catch (err) {
      setError(err.message || 'No se pudo cambiar el ámbito');
    }
  };

  /* ── Columnas ─────────────────────────────────────────────────────────── */
  const columnasModelos = useMemo(() => [
    {
      key: 'name',
      label: 'Modelo',
      sortable: true,
      render: (row) => (
        <span>
          <strong>{row.name}</strong>
          {row.sku && <span className="list-hint"> · {row.sku}</span>}
        </span>
      ),
    },
    {
      key: 'manufacturer_name',
      label: 'Marca',
      sortable: true,
      render: (row) => row.manufacturer_name || <span className="list-hint">Sin marca</span>,
    },
    {
      key: 'device_type',
      label: 'Tipo',
      sortable: true,
      sortValue: (row) => etiquetaTipo(row.device_type),
      render: (row) => <span className="list-badge">{etiquetaTipo(row.device_type)}</span>,
    },
    {
      key: 'scope',
      label: 'Ámbito',
      sortable: true,
      sortValue: (row) => SCOPE_LABEL[row.scope] || row.scope,
      render: (row) => scopeBadge(row.scope),
    },
    {
      key: 'usage',
      label: 'Equipos',
      sortable: true,
      sortValue: (row) => row.usage?.total || 0,
      render: (row) => {
        const total = row.usage?.total || 0;
        return total
          ? <span className="list-code" title="Elementos que usan este modelo">{total}</span>
          : <span className="list-hint">Sin uso</span>;
      },
    },
    {
      key: 'spec',
      label: 'Plantilla',
      render: (row) => <span className="list-hint">{specResumen(row)}</span>,
    },
    {
      key: 'is_active',
      label: 'Estado',
      sortable: true,
      sortValue: (row) => (row.is_active ? 1 : 0),
      render: (row) => (row.is_active
        ? <span className="list-badge list-badge--on">Activo</span>
        : <span className="list-badge list-badge--off">Inactivo</span>),
    },
    {
      key: 'updated_at',
      label: 'Actualizado',
      sortable: true,
      render: (row) => (row.updated_at
        ? <span className="list-hint">{new Date(row.updated_at).toLocaleDateString()}</span>
        : <span className="list-hint">—</span>),
    },
  ], [etiquetaTipo]);

  const columnasMarcas = useMemo(() => [
    { key: 'name', label: 'Marca', sortable: true, render: (row) => <strong>{row.name}</strong> },
    {
      key: 'device_type',
      label: 'Tipo',
      sortable: true,
      sortValue: (row) => etiquetaTipo(row.device_type),
      render: (row) => (row.device_type
        ? <span className="list-badge">{etiquetaTipo(row.device_type)}</span>
        : <span className="list-hint">Cualquier tipo</span>),
    },
    {
      key: 'scope',
      label: 'Ámbito',
      sortable: true,
      sortValue: (row) => SCOPE_LABEL[row.scope] || row.scope,
      render: (row) => scopeBadge(row.scope),
    },
    {
      key: 'usage',
      label: 'Equipos',
      sortable: true,
      sortValue: (row) => row.usage?.total || 0,
      render: (row) => (row.usage?.total
        ? <span className="list-code">{row.usage.total}</span>
        : <span className="list-hint">Sin uso</span>),
    },
    {
      key: 'aliases',
      label: 'Alias de inventario',
      render: (row) => ((row.aliases || []).length
        ? <span className="list-hint">{row.aliases.join(', ')}</span>
        : <span className="list-hint">—</span>),
    },
    {
      key: 'is_active',
      label: 'Estado',
      sortable: true,
      sortValue: (row) => (row.is_active ? 1 : 0),
      render: (row) => (row.is_active
        ? <span className="list-badge list-badge--on">Activa</span>
        : <span className="list-badge list-badge--off">Inactiva</span>),
    },
  ], [etiquetaTipo]);

  const esModelos = vista === 'models';
  const filas = esModelos ? modelosFiltrados : marcasFiltradas;

  const filtros = [
    {
      key: 'type', label: 'Tipo', allLabel: 'Cualquier tipo',
      value: filterType, onChange: setFilterType,
      options: deviceTypes,
    },
    {
      key: 'scope', label: 'Ámbito', allLabel: 'Todos los ámbitos',
      value: filterScope, onChange: setFilterScope, options: SCOPE_OPTIONS,
    },
    {
      key: 'enabled', label: 'Estado', allLabel: 'Activas e inactivas',
      value: filterEnabled, onChange: setFilterEnabled,
      options: [
        { value: 'on', label: 'Solo las activas' },
        { value: 'off', label: 'Solo las inactivas' },
      ],
    },
  ];

  if (esModelos) {
    filtros.splice(2, 0, {
      key: 'manufacturer', label: 'Marca', allLabel: 'Cualquier marca',
      value: filterManufacturer, onChange: setFilterManufacturer,
      options: manufacturers.map((row) => ({ value: String(row.id), label: row.name })),
    });
  }

  const exportRows = useMemo(() => filas.map((row) => ({
    'Nombre': row.name || '',
    'Marca': row.manufacturer_name || '',
    'Tipo': etiquetaTipo(row.device_type),
    'Ámbito': SCOPE_LABEL[row.scope] || row.scope,
    'Equipos': row.usage?.total || 0,
    'Plantilla': esModelos ? specResumen(row) : '',
    'Alias': (row.aliases || []).join(', '),
    'Estado': row.is_active ? 'Activo' : 'Inactivo',
  })), [filas, etiquetaTipo, esModelos]);

  if (!companyId) {
    return (
      <div className="list-page" style={{ padding: '1rem 0' }}>
        <div className="list-hint">Selecciona una empresa para gestionar el catálogo.</div>
      </div>
    );
  }

  const puedeGestionar = (row) => admin || row.scope === 'private';

  return (
    <>
      <ListPage
        icon={CpuChipIcon}
        title="Catálogo de dispositivos"
        subtitle={esModelos
          ? `${models.length} modelos · ${modelosFiltrados.length} filtrados`
          : `${manufacturers.length} marcas · ${marcasFiltradas.length} filtradas`}

        onViewChange={setVista}

        search={search}
        onSearchChange={setSearch}
        searchPlaceholder={esModelos
          ? 'Buscar por modelo, marca, SKU o alias…'
          : 'Buscar por marca o alias…'}
        filters={filtros}

        exportRows={exportRows}
        exportFileBase={esModelos ? 'catalogo-modelos' : 'catalogo-marcas'}
        exportSheetName={esModelos ? 'Modelos' : 'Marcas'}

        columns={esModelos ? columnasModelos : columnasMarcas}
        columnStorageKey={esModelos ? 'cmdb_cols_catalog_models' : 'cmdb_cols_catalog_brands'}
        defaultVisible={esModelos
          ? ['name', 'manufacturer_name', 'device_type', 'scope', 'usage', 'spec', 'is_active']
          : ['name', 'device_type', 'scope', 'usage', 'aliases', 'is_active']}
        rows={filas}
        rowKey={(row) => row.id}
        rowTitle={(row) => `Editar ${row.name}`}
        onRowClick={abrirEdicion}

        kpis={(
          <div className="list-kpis list-kpis--chips">
            <button type="button" className="list-kpi-chip" onClick={() => { setFilterScope(''); setFilterType(''); setFilterEnabled(''); setSearch(''); }} title="Ver todo el catálogo">
              <span className="list-kpi-value">{kpis.modelos}</span>
              <span className="list-kpi-label">modelos</span>
            </button>
            <button type="button" className="list-kpi-chip" onClick={() => setFilterScope('global')} title="Modelos que comparten todas las empresas">
              <span className="list-kpi-value">{kpis.globales}</span>
              <span className="list-kpi-label">globales</span>
            </button>
            <button type="button" className="list-kpi-chip" onClick={() => setFilterScope('private')} title="Modelos que solo ve su creador">
              <span className="list-kpi-value">{kpis.privados}</span>
              <span className="list-kpi-label">privados</span>
            </button>
            <div className="list-kpi-chip" title="Modelos sin plantilla: no rellenarán puertos, PoE ni PSU">
              <span className="list-kpi-value">{kpis.sinPlantilla}</span>
              <span className="list-kpi-label">sin plantilla</span>
            </div>
            <div className="list-kpi-chip" title="Modelos que no usa ningún elemento todavía">
              <span className="list-kpi-value">{kpis.sinUso}</span>
              <span className="list-kpi-label">sin uso</span>
            </div>
          </div>
        )}

        onCreate={abrirAlta}
        createLabel={esModelos ? 'Nuevo modelo' : 'Nueva marca'}
        createTitle={esModelos
          ? 'Marca, tipo y plantilla de puertos / PSU'
          : 'Marca con sus alias de inventario'}

        headerActions={(
          <>
            {aviso && <span className="list-badge list-badge--on">{aviso}</span>}
            <button type="button" className="list-btn-secondary" onClick={cargar} disabled={loading}>
              <ArrowPathIcon style={{ width: 15, height: 15 }} /> {loading ? 'Cargando…' : 'Actualizar'}
            </button>
          </>
        )}

        views={[
          { key: 'models', label: 'Modelos', icon: Squares2X2Icon, title: 'Modelos con su plantilla y sus equipos' },
          { key: 'manufacturers', label: 'Marcas', icon: TagIcon, title: 'Marcas del catálogo y sus alias' },
        ]}
        listKey="device-catalog"
        defaultView="models"

        actions={{
          extra: (row) => (
            <>
              {esModelos && (
                <button
                  type="button"
                  className="list-action-btn"
                  onClick={() => verUso(row)}
                  title="Ver cuántos equipos lo usan"
                >
                  <ChartBarIcon />
                </button>
              )}
              {esModelos && (
                <button
                  type="button"
                  className="list-action-btn"
                  onClick={() => navigate(`/docs/device_model/${row.id}`)}
                  title="Documentación del modelo (manual, datasheet…)"
                >
                  <DocumentTextIcon />
                </button>
              )}
              {admin && row.scope !== 'global' && (
                <button
                  type="button"
                  className="list-action-btn"
                  onClick={() => cambiarAmbito(row, row.scope === 'private' ? 'company' : 'global')}
                  title={row.scope === 'private' ? 'Compartir con la empresa' : 'Publicar como global'}
                >
                  <ShareIcon />
                </button>
              )}
            </>
          ),
          canEdit: puedeGestionar,
          onEdit: abrirEdicion,
          canCopy: esModelos,
          onCopy: abrirCopia,
          canDelete: puedeGestionar,
          onDelete: borrar,
          deleteTitle: 'Desactivar entrada del catálogo',
          deleteMessage: (row) => (
            <>
              ¿Desactivar <strong>{row.name}</strong>? Los equipos que ya lo usan no cambian;
              solo deja de ofrecerse en las fichas y en el inventario.
            </>
          ),
        }}

        loading={loading && models.length === 0 && manufacturers.length === 0}
        loadingText="Cargando el catálogo…"
        error={error}

        empty={{
          icon: '🧩',
          title: filas.length === 0
            ? (esModelos ? 'Todavía no hay modelos' : 'Todavía no hay marcas')
            : 'Ninguna entrada coincide con los filtros',
          subtitle: esModelos
            ? 'Un modelo guarda su marca, su tipo y su plantilla: puertos, PoE, PSU, tamaño y consumo.'
            : 'Una marca reúne sus modelos y los alias con los que aparece en el inventario.',
          action: filas.length === 0 ? (
            <button type="button" className="list-btn-primary" onClick={abrirAlta}>
              <PlusIcon style={{ width: 14, height: 14 }} /> {esModelos ? 'Nuevo modelo' : 'Nueva marca'}
            </button>
          ) : undefined,
        }}
      />

      {/* ══ Alta / edición ══ */}
      {formOpen && (
        <Dialog
          title={editando
            ? `Editar ${esModelos ? 'modelo' : 'marca'} ${editando.name}`
            : (esModelos ? 'Nuevo modelo' : 'Nueva marca')}
          icon={esModelos
            ? <CpuChipIcon style={{ width: 18, height: 18 }} />
            : <TagIcon style={{ width: 18, height: 18 }} />}
          maxWidth={760}
          busy={guardando}
          onClose={() => { setFormOpen(false); setEditando(null); }}
          footer={(
            <>
              <button type="button" className="list-btn-secondary" onClick={() => { setFormOpen(false); setEditando(null); }} disabled={guardando}>
                Cancelar
              </button>
              <button type="button" className="list-btn-primary" onClick={guardar} disabled={guardando || !form.name.trim()}>
                {guardando ? 'Guardando…' : (editando ? 'Guardar cambios' : 'Crear')}
              </button>
            </>
          )}
        >
          <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
            <label className="list-hint" style={{ flex: '1 1 240px' }}>
              {esModelos ? 'Modelo' : 'Marca'}
              <input
                className="list-input"
                style={{ width: '100%', marginTop: 4 }}
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                placeholder={esModelos ? 'Catalyst 9300-24T' : 'Cisco'}
                autoFocus
              />
            </label>
            <label className="list-hint" style={{ flex: '1 1 180px' }}>
              Tipo
              <select
                className="list-select"
                style={{ width: '100%', marginTop: 4 }}
                value={form.device_type}
                onChange={(e) => setForm({ ...form, device_type: e.target.value })}
              >
                {!esModelos && <option value="">Cualquier tipo</option>}
                {deviceTypes.map((tipo) => (
                  <option key={tipo.value} value={tipo.value}>{tipo.label}</option>
                ))}
              </select>
            </label>
          </div>

          {esModelos && (
            <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginTop: 10 }}>
              <label className="list-hint" style={{ flex: '1 1 240px' }}>
                Marca
                <select
                  className="list-select"
                  style={{ width: '100%', marginTop: 4 }}
                  value={form.manufacturer_id}
                  onChange={(e) => setForm({ ...form, manufacturer_id: e.target.value })}
                >
                  <option value="">— Sin marca —</option>
                  {manufacturers.map((marca) => (
                    <option key={marca.id} value={String(marca.id)}>{marca.name}</option>
                  ))}
                </select>
              </label>
              <label className="list-hint" style={{ flex: '1 1 200px' }}>
                Marca nueva (si no está en la lista)
                <input
                  className="list-input"
                  style={{ width: '100%', marginTop: 4 }}
                  value={form.manufacturer_name}
                  onChange={(e) => setForm({ ...form, manufacturer_name: e.target.value, manufacturer_id: '' })}
                  placeholder="Solo si la marca no existe"
                />
              </label>
            </div>
          )}

          <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginTop: 10 }}>
            <label className="list-hint" style={{ flex: '0 1 220px' }}>
              Ámbito
              <select
                className="list-select"
                style={{ width: '100%', marginTop: 4 }}
                value={form.scope}
                onChange={(e) => setForm({ ...form, scope: e.target.value })}
              >
                {SCOPE_OPTIONS
                  .filter((option) => admin || option.value === 'private')
                  .map((option) => <option key={option.value} value={option.value}>{option.label}</option>)}
              </select>
              {!admin && (
                <span className="list-hint">Un editor solo puede crear entradas privadas.</span>
              )}
            </label>
            {esModelos && (
              <label className="list-hint" style={{ flex: '1 1 200px' }}>
                Alias de inventario (separados por comas)
                <input
                  className="list-input"
                  style={{ width: '100%', marginTop: 4 }}
                  value={form.aliases}
                  onChange={(e) => setForm({ ...form, aliases: e.target.value })}
                  placeholder="C9300-24T, Catalyst 9300"
                />
              </label>
            )}
            {!esModelos && (
              <label className="list-hint" style={{ flex: '1 1 260px' }}>
                Alias de inventario (separados por comas)
                <input
                  className="list-input"
                  style={{ width: '100%', marginTop: 4 }}
                  value={form.aliases}
                  onChange={(e) => setForm({ ...form, aliases: e.target.value })}
                  placeholder="HP, Hewlett-Packard"
                />
              </label>
            )}
          </div>

          {esModelos && (
            <>
              <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginTop: 10 }}>
                <label className="list-hint" style={{ flex: '1 1 160px' }}>
                  SKU / part number
                  <input
                    className="list-input"
                    style={{ width: '100%', marginTop: 4 }}
                    value={form.sku}
                    onChange={(e) => setForm({ ...form, sku: e.target.value })}
                  />
                </label>
                <label className="list-hint" style={{ flex: '0 1 110px' }}>
                  Tamaño (U)
                  <input
                    className="list-input"
                    type="number"
                    min="1"
                    style={{ width: '100%', marginTop: 4 }}
                    value={form.size_u}
                    onChange={(e) => setForm({ ...form, size_u: e.target.value })}
                  />
                </label>
                <label className="list-hint" style={{ flex: '0 1 130px' }}>
                  Consumo (W)
                  <input
                    className="list-input"
                    type="number"
                    min="0"
                    style={{ width: '100%', marginTop: 4 }}
                    value={form.power_watts}
                    onChange={(e) => setForm({ ...form, power_watts: e.target.value })}
                  />
                </label>
              </div>

              {TIPOS_CON_PUERTOS.has(form.device_type) && (
                <fieldset style={{ border: '1px solid var(--border)', borderRadius: 8, padding: 10, marginTop: 12 }}>
                  <legend className="list-hint" style={{ padding: '0 6px' }}>
                    {form.device_type === 'pdu' ? 'Enchufes' : 'Puertos'}
                  </legend>
                  <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
                    <label className="list-hint" style={{ flex: '0 1 110px' }}>
                      {form.device_type === 'pdu' ? 'Nº enchufes' : 'Nº puertos'}
                      <input
                        className="list-input"
                        type="number"
                        min="0"
                        style={{ width: '100%', marginTop: 4 }}
                        value={form.port_count}
                        onChange={(e) => setForm({ ...form, port_count: e.target.value })}
                      />
                    </label>
                    {form.device_type !== 'pdu' && (
                      <>
                        <label className="list-hint" style={{ flex: '0 1 150px' }}>
                          Tipo
                          <select
                            className="list-select"
                            style={{ width: '100%', marginTop: 4 }}
                            value={form.port_type}
                            onChange={(e) => setForm({ ...form, port_type: e.target.value })}
                          >
                            {PORT_TYPE_OPTIONS.map((option) => (
                              <option key={option.value} value={option.value}>{option.label}</option>
                            ))}
                          </select>
                        </label>
                        <label className="list-hint" style={{ flex: '0 1 150px' }}>
                          Velocidad
                          <select
                            className="list-select"
                            style={{ width: '100%', marginTop: 4 }}
                            value={form.port_speed}
                            onChange={(e) => setForm({ ...form, port_speed: e.target.value })}
                          >
                            {SPEED_OPTIONS.map((option) => (
                              <option key={option.value || 'none'} value={option.value}>{option.label}</option>
                            ))}
                          </select>
                        </label>
                        <label className="list-hint" style={{ flex: '0 1 120px', display: 'flex', alignItems: 'center', gap: 6, marginTop: 18 }}>
                          <input
                            type="checkbox"
                            checked={form.port_poe}
                            onChange={(e) => setForm({ ...form, port_poe: e.target.checked, poe_capable: e.target.checked || form.poe_capable })}
                          />
                          PoE por puerto
                        </label>
                      </>
                    )}
                  </div>
                  {form.device_type !== 'pdu' && (
                    <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginTop: 8 }}>
                      <label className="list-hint" style={{ flex: '0 1 130px' }}>
                        Puertos uplink
                        <input
                          className="list-input"
                          type="number"
                          min="0"
                          style={{ width: '100%', marginTop: 4 }}
                          value={form.uplink_count}
                          onChange={(e) => setForm({ ...form, uplink_count: e.target.value })}
                        />
                      </label>
                      <label className="list-hint" style={{ flex: '0 1 150px' }}>
                        Tipo uplink
                        <select
                          className="list-select"
                          style={{ width: '100%', marginTop: 4 }}
                          value={form.uplink_type}
                          onChange={(e) => setForm({ ...form, uplink_type: e.target.value })}
                        >
                          {PORT_TYPE_OPTIONS.map((option) => (
                            <option key={option.value} value={option.value}>{option.label}</option>
                          ))}
                        </select>
                      </label>
                      <label className="list-hint" style={{ flex: '0 1 150px' }}>
                        Velocidad uplink
                        <select
                          className="list-select"
                          style={{ width: '100%', marginTop: 4 }}
                          value={form.uplink_speed}
                          onChange={(e) => setForm({ ...form, uplink_speed: e.target.value })}
                        >
                          {SPEED_OPTIONS.map((option) => (
                            <option key={option.value || 'none'} value={option.value}>{option.label}</option>
                          ))}
                        </select>
                      </label>
                    </div>
                  )}
                  {form.device_type === 'switch' && (
                    <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginTop: 8 }}>
                      <label className="list-hint" style={{ flex: '0 1 160px', display: 'flex', alignItems: 'center', gap: 6 }}>
                        <input
                          type="checkbox"
                          checked={form.poe_capable}
                          onChange={(e) => setForm({ ...form, poe_capable: e.target.checked })}
                        />
                        Con PoE
                      </label>
                      <label className="list-hint" style={{ flex: '0 1 150px' }}>
                        Presupuesto PoE (W)
                        <input
                          className="list-input"
                          type="number"
                          min="0"
                          style={{ width: '100%', marginTop: 4 }}
                          value={form.poe_budget_w}
                          onChange={(e) => setForm({ ...form, poe_budget_w: e.target.value })}
                          disabled={!form.poe_capable}
                        />
                      </label>
                    </div>
                  )}
                </fieldset>
              )}

              {TIPOS_CON_PSU.has(form.device_type) && (
                <fieldset style={{ border: '1px solid var(--border)', borderRadius: 8, padding: 10, marginTop: 12 }}>
                  <legend className="list-hint" style={{ padding: '0 6px' }}>Fuentes de alimentación</legend>
                  <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
                    <label className="list-hint" style={{ flex: '0 1 100px' }}>
                      Nº de PSU
                      <input
                        className="list-input"
                        type="number"
                        min="0"
                        style={{ width: '100%', marginTop: 4 }}
                        value={form.psu_count}
                        onChange={(e) => setForm({ ...form, psu_count: e.target.value })}
                      />
                    </label>
                    <label className="list-hint" style={{ flex: '0 1 110px' }}>
                      Vatios
                      <input
                        className="list-input"
                        type="number"
                        min="0"
                        style={{ width: '100%', marginTop: 4 }}
                        value={form.psu_watts}
                        onChange={(e) => setForm({ ...form, psu_watts: e.target.value })}
                      />
                    </label>
                    <label className="list-hint" style={{ flex: '0 1 140px' }}>
                      Conector
                      <input
                        className="list-input"
                        style={{ width: '100%', marginTop: 4 }}
                        value={form.psu_connector}
                        onChange={(e) => setForm({ ...form, psu_connector: e.target.value })}
                        placeholder="IEC C14"
                      />
                    </label>
                    <label className="list-hint" style={{ flex: '0 1 150px' }}>
                      Eficiencia
                      <input
                        className="list-input"
                        style={{ width: '100%', marginTop: 4 }}
                        value={form.psu_efficiency}
                        onChange={(e) => setForm({ ...form, psu_efficiency: e.target.value })}
                        placeholder="80+ Platinum"
                      />
                    </label>
                    <label className="list-hint" style={{ flex: '0 1 150px', display: 'flex', alignItems: 'center', gap: 6, marginTop: 18 }}>
                      <input
                        type="checkbox"
                        checked={form.psu_redundant}
                        onChange={(e) => setForm({ ...form, psu_redundant: e.target.checked })}
                      />
                      Redundantes
                    </label>
                  </div>
                </fieldset>
              )}

              <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginTop: 10 }}>
                <label className="list-hint" style={{ flex: '1 1 260px' }}>
                  Ficha técnica (URL)
                  <input
                    className="list-input"
                    style={{ width: '100%', marginTop: 4 }}
                    value={form.datasheet_url}
                    onChange={(e) => setForm({ ...form, datasheet_url: e.target.value })}
                    placeholder="https://…"
                  />
                </label>
                <label className="list-hint" style={{ flex: '1 1 200px' }}>
                  Imagen (URL)
                  <input
                    className="list-input"
                    style={{ width: '100%', marginTop: 4 }}
                    value={form.image_url}
                    onChange={(e) => setForm({ ...form, image_url: e.target.value })}
                  />
                </label>
              </div>
            </>
          )}

          <label className="list-hint" style={{ display: 'block', marginTop: 10 }}>
            Notas
            <textarea
              className="list-input"
              rows={2}
              style={{ width: '100%', marginTop: 4, resize: 'vertical' }}
              value={form.notes}
              onChange={(e) => setForm({ ...form, notes: e.target.value })}
            />
          </label>

          {error && <div className="list-hint" style={{ color: 'var(--danger, #dc2626)', marginTop: 8 }}>{error}</div>}
        </Dialog>
      )}

      {/* ══ Uso del modelo ══ */}
      {uso && (
        <Dialog
          title={`Uso de ${uso.row.name}`}
          icon={<ChartBarIcon style={{ width: 18, height: 18 }} />}
          maxWidth={420}
          onClose={() => setUso(null)}
          footer={(
            <button type="button" className="list-btn-secondary" onClick={() => setUso(null)}>
              Cerrar
            </button>
          )}
        >
          {uso.loading && <span className="list-hint">Calculando…</span>}
          {!uso.loading && uso.data && (
            <>
              <div style={{ display: 'flex', alignItems: 'baseline', gap: 8 }}>
                <span style={{ fontSize: 28, fontWeight: 700 }}>{uso.data.total || 0}</span>
                <span className="list-hint">elementos usan este modelo</span>
              </div>
              <div style={{ marginTop: 10 }}>
                {Object.entries(uso.data.by_source || {}).map(([source, count]) => (
                  <div key={source} style={{ display: 'flex', justifyContent: 'space-between', padding: '4px 0' }}>
                    <span className="list-hint">{source}</span>
                    <span className="list-code">{count}</span>
                  </div>
                ))}
                {!Object.keys(uso.data.by_source || {}).length && (
                  <span className="list-hint">
                    Ningún elemento lo usa todavía. Se aplicará solo cuando se cree uno con este modelo
                    (a mano o por inventario).
                  </span>
                )}
              </div>
            </>
          )}
        </Dialog>
      )}

      {/* ══ Copiar modelo ══ */}
      {copiando && (
        <Dialog
          title={`Copiar ${copiando.name}`}
          icon={<CpuChipIcon style={{ width: 18, height: 18 }} />}
          maxWidth={460}
          busy={guardando}
          onClose={() => setCopiando(null)}
          footer={(
            <>
              <button type="button" className="list-btn-secondary" onClick={() => setCopiando(null)} disabled={guardando}>
                Cancelar
              </button>
              <button type="button" className="list-btn-primary" onClick={confirmarCopia} disabled={guardando || !nombreCopia.trim()}>
                {guardando ? 'Copiando…' : 'Copiar'}
              </button>
            </>
          )}
        >
          <p className="list-hint" style={{ marginTop: 0 }}>
            Se copia la plantilla, los alias y la marca. <strong>No</strong> se copia el SKU
            (es la identidad comercial del modelo) ni la documentación.
          </p>
          <input
            className="list-input"
            style={{ width: '100%' }}
            value={nombreCopia}
            onChange={(e) => setNombreCopia(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') confirmarCopia(); }}
            autoFocus
          />
        </Dialog>
      )}
    </>
  );
}
